/**
 * External File Search Agent
 *
 * Sub-agent for searching uploaded external files (PDFs, Excel, CSV, Word, PowerPoint).
 * Uses vector search, fuzzy matching, and exact phrase search to find relevant content.
 *
 * Implements the Agent-as-Tool pattern via AgentBaseClass.
 *
 * ## Asset ID Prefixes
 * - page_*: PDF, Word, or PowerPoint page chunks
 * - excel_*: Excel sheet data chunks
 *
 * ## Usage
 * ```typescript
 * const result = await externalFileSearchAgent.execute({
 *     requirements: [
 *         { id: 'A', requirement: 'Find revenue projections for next 5 years' },
 *         { id: 'B', requirement: 'Search for EBITDA margin analysis' }
 *     ],
 *     context: [
 *         { context: ['as_abc12'], purpose: 'AlphaSense data to validate' }
 *     ]
 * }, callbacks);
 * ```
 */
import { AgentBaseClass } from "../../base/agentBaseClass";
import { ToolDescription } from "../../base/toolDescription";
import { ExternalFileSearchParamsSchema } from "./externalFileSearch.schema";
import { SearchFilesTool, ReadExcelTool, } from "./tools";
import GlobalStateManager from "../../../globals/global-state/globalStateManager";
import { buildExternalFileSystemPrompt } from "./prompt";
// ═══════════════════════════════════════════════════════════════════════════
// EXTERNAL FILE SEARCH AGENT
// ═══════════════════════════════════════════════════════════════════════════
// System prompt for agent-as-tool description (stored in Keywords AI)
const EXTERNAL_FILE_SEARCH_AGENT_TOOL_PROMPT = `Search uploaded external files (PDFs, Excel, CSV, Word, PowerPoint).

Use this agent when you need to:
- Find data in uploaded documents (revenue, metrics, analysis)
- Search across multiple file types
- Extract specific information from Excel sheets
- Locate content in PDF reports or presentations

Returns document IDs (page_*, excel_*) for use in slide creation.`;
/**
 * External File Search Agent
 *
 * Searches uploaded external files (PDFs, Excel, CSV, Word, PowerPoint)
 * for content matching user requirements.
 */
export class ExternalFileSearchAgent extends AgentBaseClass {
    /**
     * Get reminder options - include external files list so agent knows what files are available
     */
    getReminderOptions() {
        return {
            includePresentation: true,
            includeSecurityWarning: true,
            includeCustomInstructions: false,
            includeDeckRegistry: false,
            includeExternalFiles: true, // ✅ Agent needs to know what files exist
        };
    }
    constructor() {
        super();
        // ═══════════════════════════════════════════════════════════════════════
        // AGENT-AS-TOOL PROMPT CONFIGURATION
        // ═══════════════════════════════════════════════════════════════════════
        /** System prompt description for agent-as-tool */
        this.systemPromptDescription = EXTERNAL_FILE_SEARCH_AGENT_TOOL_PROMPT;
        // ═══════════════════════════════════════════════════════════════════════
        // REQUIRED IMPLEMENTATIONS
        // ═══════════════════════════════════════════════════════════════════════
        this.agentType = 'external_file';
        this.name = 'externalFileSearchAgent';
        this.isAgent = true; // Mark as agent-as-tool
        this.createsUndoCheckpoint = false; // Read-only search agent
        // Tools this agent can use (submitResults is injected by base class)
        this.toolCalls = [
            new SearchFilesTool(),
            new ReadExcelTool({ mode: "filepath" }),
        ];
        // ═══════════════════════════════════════════════════════════════════════
        // STREAMING FIELD CONFIGURATION (for Input Parameters UI)
        // ═══════════════════════════════════════════════════════════════════════
        /** Define fields to extract during streaming for progressive UI updates */
        this.fieldsToCheck = [
            { name: 'threadId', type: 'string', list_status: false, order: 2 },
        ];
        /** Define list fields - items committed individually as they stream in */
        this.listsToCheck = [
            {
                name: 'requirements',
                type: 'array',
                item_type: 'object',
                list_status: true,
                order: 1,
                check_sub_field: 'requirement' // Item ready when 'requirement' field exists
            },
            {
                name: 'context',
                type: 'array',
                item_type: 'object',
                list_status: true,
                order: 3,
                check_sub_field: 'purpose'
            },
        ];
        // Enable infrastructure for agent-as-tool pattern
        this.useThreadManagement = true;
        this.useTaskTracking = true;
        this.useContextResolution = true;
        // Enable standard submitResults pattern (injected by base class)
        this.useSubmitResultsPattern = true;
        // Enable streaming for real-time UI updates
        this.stream = true;
        // Allow longer responses for document analysis
        // maxResponseTokens: inherits 0 from base → adapter uses model maximum
        // Track gathered documents during execution
        this.gatheredDocuments = new Map();
        this.description = new ToolDescription(this.name, this.systemPromptDescription, ExternalFileSearchParamsSchema, {
            briefDescription: "Search uploaded files (PDFs, Excel, Word)",
            whenToUse: [
                "Finding data in uploaded documents",
                "Searching Excel sheets for content",
                "Locating content in PDF reports",
            ],
            exampleUsage: '{"requirements": [{"id": "A", "requirement": "Find Q4 revenue data"}]}',
            commonMistakes: [
                "Searching when no files are uploaded",
            ],
            capabilities: [
                "Vector/fuzzy/exact search",
                "Excel reading",
                "PDF/Word parsing",
            ],
        });
        // Set paramsSchema for agent-as-tool pattern
        this.paramsSchema = ExternalFileSearchParamsSchema;
        // Initialize agent to inject submitResults tool
        this.initializeAgent();
    }
    /**
     * Generate system prompt for the External File Search agent
     * Uses the new standardized 5-section prompt structure
     */
    generateSystemPrompt(resolvedContext) {
        return buildExternalFileSystemPrompt({ resolvedContext, tools: this.toolCalls });
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TOOL RESULT HANDLING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Generate a composite ID for Excel read results.
     * Format: f{fileIndex}|excelread_{opType}|{identifier}
     *
     * Uses | as hierarchy separator because it never appears in Excel identifiers.
     * The identifier is kept in its natural form (e.g., "Sheet1!A1:B10") for readability.
     *
     * Examples:
     * - f0|excelread_content|Sheet1!A1:B10
     * - f0|excelread_chart|Chart 1
     * - f1|excelread_formula|Financials!D5
     */
    generateExcelReadId(fileIndex, opType, identifier) {
        const fileRef = typeof fileIndex === 'number' ? `f${fileIndex}` : `f${fileIndex}`;
        return `${fileRef}|excelread_${opType}|${identifier}`;
    }
    /**
     * Handle tool results - store document references for context resolution
     * Note: submitResults is handled by the base class
     */
    onToolResult(toolName, result) {
        // Handle searchFiles results - store document references
        if (toolName === 'searchFiles' && result) {
            const searchResult = result;
            if (searchResult.success && searchResult.results) {
                for (const doc of searchResult.results) {
                    const storedData = {
                        id: doc.pageId,
                        content: doc.content,
                        metadata: {
                            filename: doc.filename,
                            pageNumber: doc.pageNumber,
                            sheetName: doc.sheetName,
                        },
                        createdAt: new Date().toISOString(),
                    };
                    this.gatheredDocuments.set(doc.pageId, storedData);
                    GlobalStateManager.getInstance().addOrUpdateContext(doc.pageId, storedData);
                    console.log(`[ExternalFileSearchAgent] Stored document: ${doc.pageId}`);
                }
            }
        }
        // Handle readExcel results - store Excel data references
        // Response format: { success, results: { readContent?, readFormatting?, listCharts?, readCharts?, formulaTrace?, tableAnalysis?, listSheets? } }
        if (toolName === 'readExcel' && result) {
            const excelResult = result;
            if (!excelResult.success || !excelResult.results)
                return;
            const results = excelResult.results;
            // Process readContent results
            if (results.readContent) {
                for (const item of results.readContent) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    const identifier = item.sheetName && item.targetRange
                        ? `${item.sheetName}!${item.targetRange}`
                        : item.sheetName || 'unknown';
                    const excelId = this.generateExcelReadId(fileIndex, 'content', identifier);
                    this.storeExcelReadResult(excelId, item, 'content', identifier);
                }
            }
            // Process readFormatting results
            if (results.readFormatting) {
                for (const item of results.readFormatting) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    const identifier = item.sheetName && item.targetRange
                        ? `${item.sheetName}!${item.targetRange}`
                        : item.sheetName || 'unknown';
                    const excelId = this.generateExcelReadId(fileIndex, 'format', identifier);
                    this.storeExcelReadResult(excelId, item, 'format', identifier);
                }
            }
            // Process listCharts results
            if (results.listCharts) {
                for (const item of results.listCharts) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    const identifier = item.sheetName || "unknown_sheet";
                    const excelId = this.generateExcelReadId(fileIndex, 'charts', identifier);
                    this.storeExcelReadResult(excelId, item, 'charts', identifier);
                }
            }
            // Process readCharts results
            if (results.readCharts) {
                for (const item of results.readCharts) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    // Charts use chartName from result, or chart name from data, or sheet name as fallback
                    const chartData = item.data;
                    const identifier = item.chartId || item.chartName || chartData?.name || item.sheetName || 'chart';
                    const excelId = this.generateExcelReadId(fileIndex, 'chart', identifier);
                    this.storeExcelReadResult(excelId, item, 'chart', identifier);
                }
            }
            // Process formulaTrace results
            if (results.formulaTrace) {
                for (const item of results.formulaTrace) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    const identifier = item.sheetName && item.targetRange
                        ? `${item.sheetName}!${item.targetRange}`
                        : item.sheetName || 'unknown';
                    const excelId = this.generateExcelReadId(fileIndex, 'formula', identifier);
                    this.storeExcelReadResult(excelId, item, 'formula', identifier);
                }
            }
            // Process tableAnalysis results
            if (results.tableAnalysis) {
                for (const item of results.tableAnalysis) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    const identifier = item.sheetName && item.targetRange
                        ? `${item.sheetName}!${item.targetRange}`
                        : item.sheetName || 'unknown';
                    const excelId = this.generateExcelReadId(fileIndex, 'table', identifier);
                    this.storeExcelReadResult(excelId, item, 'table', identifier);
                }
            }
            // Process listSheets results
            if (results.listSheets) {
                for (const item of results.listSheets) {
                    if (!item.success)
                        continue;
                    const fileIndex = item.fileIndex ?? 0;
                    const excelId = this.generateExcelReadId(fileIndex, 'sheets', 'all');
                    this.storeExcelReadResult(excelId, item, 'sheets', 'all');
                }
            }
        }
    }
    /**
     * Store an Excel read result in gathered documents and global state
     */
    storeExcelReadResult(excelId, item, opType, identifier) {
        const content = item.asString ??
            (item.data !== undefined ? JSON.stringify(item.data) : JSON.stringify(item));
        const storedData = {
            id: excelId,
            content,
            metadata: { opType, identifier },
            createdAt: new Date().toISOString(),
        };
        this.gatheredDocuments.set(excelId, storedData);
        GlobalStateManager.getInstance().addOrUpdateContext(excelId, storedData);
        console.log(`[ExternalFileSearchAgent] Stored Excel read: ${excelId}`);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // HOOKS - Override base class hooks
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Prepare for execution - clear previous state
     */
    async beforeExecute(context) {
        console.log(`[ExternalFileSearchAgent] Starting execution with ${context.params.requirements?.length || 0} requirements`);
        if (context.params.context && context.params.context.length > 0) {
            console.log(`[ExternalFileSearchAgent] Context provided: ${context.params.context.length} items`);
        }
        this.gatheredDocuments.clear();
    }
    /**
     * Build final result with context ID note for orchestrator
     */
    buildFinalResult(results, threadId, tokensUsed, summary) {
        if (results.length === 0) {
            return this.buildPartialResult(results, threadId, tokensUsed, summary);
        }
        const result = this.buildResult(results, threadId, tokensUsed, summary);
        result._contextIdNote = "The document IDs (page_*, excel_*) can be passed to execution agents for content insertion. Store in Asset DB for retrieval.";
        return result;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // RESULT PROCESSING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Process results - fallback when submitResults wasn't called
     * Note: If LLM properly calls submitResults, processSubmittedResults is used instead
     */
    async processResults(responseContent, params, context) {
        // Fallback: LLM didn't call submitResults properly
        console.warn('[ExternalFileSearchAgent] submitResults not called - using fallback processing');
        return this.processFallbackResults(responseContent, params, context);
    }
    /**
     * Fallback processing when submitResults wasn't called
     */
    processFallbackResults(responseContent, params, context) {
        const results = [];
        const allDocIds = Array.from(this.gatheredDocuments.keys());
        for (const req of params.requirements) {
            context.cancellationToken.throwIfCancelled();
            // Try to find document IDs mentioned in response for this requirement
            const mentionedIds = this.extractDocIdsFromResponse(responseContent, allDocIds);
            const relevantIds = mentionedIds.length > 0 ? mentionedIds : allDocIds;
            const documents = [];
            for (const docId of relevantIds) {
                const storedDoc = this.gatheredDocuments.get(docId);
                if (storedDoc) {
                    documents.push({
                        documentId: storedDoc.id,
                        filename: storedDoc.metadata?.filename,
                        location: this.getLocationFromDocId(storedDoc.id, storedDoc),
                        snippet: storedDoc.content?.substring(0, 200),
                    });
                }
            }
            results.push({
                requirementId: req.id,
                relevantResults: {
                    documents,
                    rationale: `Found ${documents.length} documents for: ${req.requirement}`,
                },
                rationale: `Found ${documents.length} documents for: ${req.requirement}`,
            });
            console.log(`[ExternalFileSearchAgent] Requirement ${req.id}: ${documents.length} documents (fallback path)`);
        }
        return results;
    }
    /**
     * Process submitted results from submitResults tool (called by base class)
     */
    async processSubmittedResults(submittedResults, _params, context) {
        console.log(`[ExternalFileSearchAgent] Processing ${submittedResults.results.length} submitted results`);
        const results = [];
        for (const submitted of submittedResults.results) {
            context.cancellationToken.throwIfCancelled();
            const documents = [];
            const searchResults = submitted.relevantResults?.searchResults;
            if (Array.isArray(searchResults)) {
                for (const docIdOrObj of searchResults) {
                    const docId = typeof docIdOrObj === 'string'
                        ? docIdOrObj
                        : docIdOrObj?.id;
                    if (docId) {
                        const storedDoc = this.gatheredDocuments.get(docId);
                        if (storedDoc) {
                            documents.push({
                                documentId: storedDoc.id,
                                filename: storedDoc.metadata?.filename,
                                location: this.getLocationFromDocId(storedDoc.id, storedDoc),
                                snippet: storedDoc.content?.substring(0, 200),
                            });
                        }
                    }
                }
            }
            results.push({
                requirementId: submitted.requirementId,
                relevantResults: {
                    documents,
                    rationale: submitted.rationale,
                },
                rationale: submitted.rationale,
            });
        }
        return results;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // HELPER METHODS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get human-readable location from document ID and stored data
     */
    getLocationFromDocId(docId, storedDoc) {
        if (docId.startsWith('page_')) {
            const pageNum = storedDoc?.metadata?.pageNumber;
            return pageNum ? `Page ${pageNum}` : 'Document page';
        }
        if (docId.startsWith('excel_')) {
            const sheetName = storedDoc?.metadata?.sheetName || storedDoc?.metadata?.range;
            return sheetName ? `Sheet: ${sheetName}` : 'Excel data';
        }
        return 'Unknown location';
    }
    /**
     * Extract document IDs mentioned in the LLM response
     */
    extractDocIdsFromResponse(response, validDocIds) {
        const mentioned = [];
        // Look for known prefixes
        const docIdPatterns = [
            /page_[a-z0-9_]+/g,
            /excel_[a-z0-9_]+/g,
        ];
        for (const pattern of docIdPatterns) {
            const matches = response.match(pattern) || [];
            for (const match of matches) {
                if (validDocIds.includes(match) && !mentioned.includes(match)) {
                    mentioned.push(match);
                }
            }
        }
        return mentioned;
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════
export default ExternalFileSearchAgent;
